import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { SERVER_URL } from "../../constants.js";

// Resident-mix analytics. The detail (who's where / who's doing what) lives in Bedrooms
// and the Activities planner now; this view answers "how many people are on site across
// a date range, and what's the mix" via cyclable charts.
//
// Data contract — GET api/residentmix/{startDate}/{endDate}  (see
// docs/backend-ticket-resident-mix.md). Until that endpoint exists the page renders
// representative SAMPLE data so the layout can be reviewed; it switches to live data
// automatically once the endpoint returns rows.

const COLORS = {
  total: "#1565c0",
  students: "#1976d2",
  adults: "#fbc02d",
  staff: "#00897b",
  male: "#42a5f5",
  female: "#ec407a",
  resident: "#2e7d32",
  nonResident: "#bdbdbd",
  groups: "#7b1fa2",
  individuals: "#ef6c00",
  nationality: "#00838f",
};

// Distinct colours for the per-campus stack on the Total residents chart.
const CAMPUS_PALETTE = [
  "#1976d2",
  "#2e7d32",
  "#ef6c00",
  "#7b1fa2",
  "#00838f",
  "#c2185b",
  "#5d4037",
  "#455a64",
];

// Distinct colours for the nationality stack (top nationalities + grey "Other" last).
const NAT_PALETTE = [
  "#1976d2",
  "#2e7d32",
  "#ef6c00",
  "#7b1fa2",
  "#c2185b",
  "#00838f",
  "#fbc02d",
  "#5d4037",
  "#455a64",
  "#d32f2f",
];

const CHARTS = [
  { key: "total", label: "Total residents" },
  { key: "role", label: "Students / adults / staff" },
  { key: "gender", label: "Male vs female" },
  { key: "residential", label: "Residents vs non-residents" },
  { key: "nationality", label: "Nationality mix" },
  { key: "groups", label: "Groups vs individuals" },
];

const METRICS = ["total", "students", "adults", "staff", "male", "female", "resident", "nonResident", "groups", "individuals"];

export default function Residents() {
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(13, "day"));
  const [campus, setCampus] = useState("ALL");
  const [excludeStaff, setExcludeStaff] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Distinguishes "the request failed" from "there is nobody here", which the
  // old code collapsed into one path and then answered by inventing a
  // population. Null means neither has happened yet.
  const [loadError, setLoadError] = useState(null);
  const [chartIdx, setChartIdx] = useState(0);

  const fetchMix = useCallback(async () => {
    if (!startDate || !endDate) return;
    const s = startDate.format("YYYY-MM-DD");
    const e = endDate.format("YYYY-MM-DD");
    if (e < s) return;
    setLoading(true);
    const token = sessionStorage.getItem("bearer");
    try {
      const res = await fetch(
        `${SERVER_URL}api/residentmix/${s}/${e}${excludeStaff ? "?excludeStaff=true" : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        throw new Error(`residentmix returned ${res.status}`);
      }
      const data = await res.json();
      // An empty array is a real answer - nobody is on site in this range - and
      // renders as an empty state below. Previously it took the same branch as
      // a failure and was answered with fabricated occupancy figures.
      setRows(Array.isArray(data) ? data : []);
      setLoadError(null);
    } catch (err) {
      console.error("[Residents] residentmix fetch failed:", err);
      setRows([]);
      setLoadError(err?.message || "Could not load resident figures.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, excludeStaff]);

  useEffect(() => {
    fetchMix();
  }, [fetchMix]);

  const campuses = useMemo(
    () => Array.from(new Set(rows.map((r) => r.campus).filter(Boolean))).sort(),
    [rows]
  );

  // Per-date series for the selected campus (summing across campuses for "All").
  const series = useMemo(() => {
    const filtered = campus === "ALL" ? rows : rows.filter((r) => r.campus === campus);
    const byDate = new Map();
    filtered.forEach((r) => {
      if (!byDate.has(r.date)) {
        const seed = { date: r.date, byNationality: {} };
        METRICS.forEach((m) => (seed[m] = 0));
        byDate.set(r.date, seed);
      }
      const o = byDate.get(r.date);
      METRICS.forEach((m) => (o[m] += Number(r[m]) || 0));
      Object.entries(r.byNationality || {}).forEach(([k, v]) => {
        o.byNationality[k] = (o.byNationality[k] || 0) + (Number(v) || 0);
      });
    });
    return Array.from(byDate.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((o) => ({ ...o, label: dayjs(o.date).format("MMM D") }));
  }, [rows, campus]);

  // Nationality totals across the whole range (top 10 + Other).
  const natData = useMemo(() => {
    const totals = {};
    series.forEach((o) =>
      Object.entries(o.byNationality || {}).forEach(([k, v]) => {
        totals[k] = (totals[k] || 0) + v;
      })
    );
    const sorted = Object.entries(totals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    if (sorted.length <= 11) return sorted;
    const top = sorted.slice(0, 10);
    const other = sorted.slice(10).reduce((s, x) => s + x.value, 0);
    return [...top, { name: "Other", value: other }];
  }, [series]);

  // Per-campus totals per day (for the Total chart's campus stack). When a single campus
  // is selected the stack is just that one campus.
  const campusKeys = useMemo(
    () => (campus === "ALL" ? campuses : [campus]).filter(Boolean),
    [campus, campuses]
  );
  const campusSeries = useMemo(() => {
    const filtered = campus === "ALL" ? rows : rows.filter((r) => r.campus === campus);
    const byDate = new Map();
    filtered.forEach((r) => {
      if (!byDate.has(r.date)) {
        byDate.set(r.date, { date: r.date, label: dayjs(r.date).format("MMM D"), total: 0 });
      }
      const o = byDate.get(r.date);
      o[r.campus] = (o[r.campus] || 0) + (Number(r.total) || 0);
      o.total += Number(r.total) || 0;
    });
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows, campus]);

  // Nationality as a per-day stacked bar: top nationalities (from natData) as segments,
  // the long tail folded into "Other".
  const natKeys = useMemo(() => natData.map((d) => d.name), [natData]);
  const natSeries = useMemo(() => {
    const topSet = new Set(natKeys.filter((k) => k !== "Other"));
    const hasOther = natKeys.includes("Other");
    return series.map((o) => {
      const row = { label: o.label };
      let other = 0;
      Object.entries(o.byNationality || {}).forEach(([k, v]) => {
        if (topSet.has(k)) row[k] = (row[k] || 0) + v;
        else other += v;
      });
      topSet.forEach((k) => {
        if (row[k] == null) row[k] = 0;
      });
      if (hasOther) row.Other = other;
      return row;
    });
  }, [series, natKeys]);

  const chart = CHARTS[chartIdx];
  const step = (dir) => setChartIdx((i) => (i + dir + CHARTS.length) % CHARTS.length);

  const stacked = (bars) => (
    <ResponsiveContainer width="100%" height={380}>
      <BarChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip />
        <Legend />
        {bars.map((b) => (
          <Bar key={b.key} dataKey={b.key} name={b.name} stackId="a" fill={COLORS[b.key]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  const renderChart = () => {
    if (!series.length) {
      return (
        <Typography variant="body2" sx={{ opacity: 0.7, p: 4, textAlign: "center" }}>
          No data for this range.
        </Typography>
      );
    }
    switch (chart.key) {
      case "total":
        return (
          <ResponsiveContainer width="100%" height={380}>
            <ComposedChart data={campusSeries} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {campusKeys.map((c, i) => (
                <Bar key={c} dataKey={c} name={c} stackId="a" fill={CAMPUS_PALETTE[i % CAMPUS_PALETTE.length]} />
              ))}
              <Line
                type="monotone"
                dataKey="total"
                name="Total"
                stroke="#212121"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        );
      case "role":
        return stacked([
          { key: "students", name: "Students" },
          { key: "adults", name: "Adults" },
          { key: "staff", name: "Staff" },
        ]);
      case "gender":
        return stacked([
          { key: "male", name: "Male" },
          { key: "female", name: "Female" },
        ]);
      case "residential":
        return stacked([
          { key: "resident", name: "Resident" },
          { key: "nonResident", name: "Non-resident" },
        ]);
      case "groups":
        return stacked([
          { key: "groups", name: "In a group" },
          { key: "individuals", name: "Individuals" },
        ]);
      case "nationality":
        return (
          <ResponsiveContainer width="100%" height={380}>
            <BarChart data={natSeries} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {natKeys.map((k, i) => (
                <Bar
                  key={k}
                  dataKey={k}
                  name={k}
                  stackId="a"
                  fill={k === "Other" ? "#9e9e9e" : NAT_PALETTE[i % NAT_PALETTE.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, pt: "80px", maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Resident mix
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
          <DatePicker
            label="From"
            value={startDate}
            onChange={(d) => d && setStartDate(d)}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
          <DatePicker
            label="To"
            value={endDate}
            onChange={(d) => d && setEndDate(d)}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="res-campus">Campus</InputLabel>
            <Select labelId="res-campus" label="Campus" value={campus} onChange={(e) => setCampus(e.target.value)}>
              <MenuItem value="ALL">All campuses</MenuItem>
              {campuses.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={excludeStaff}
                onChange={(e) => setExcludeStaff(e.target.checked)}
              />
            }
            label="Exclude staff"
          />
          {loading && <CircularProgress size={20} />}
        </Stack>
      </LocalizationProvider>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load resident figures, so nothing is charted below. {loadError}
        </Alert>
      )}

      {!loadError && !loading && rows.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No residents on site between {startDate?.format("D MMM YYYY")} and{" "}
          {endDate?.format("D MMM YYYY")}
          {campus !== "ALL" ? ` at ${campus}` : ""}. Nothing to chart.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
          <IconButton size="small" onClick={() => step(-1)} aria-label="Previous chart">
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ minWidth: 220, textAlign: "center", fontWeight: 600 }}>
            {chart.label}
          </Typography>
          <IconButton size="small" onClick={() => step(1)} aria-label="Next chart">
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mb: 1 }}>
          {CHARTS.map((c, i) => (
            <Box
              key={c.key}
              onClick={() => setChartIdx(i)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                cursor: "pointer",
                bgcolor: i === chartIdx ? "primary.main" : "action.disabled",
              }}
            />
          ))}
        </Stack>
        {renderChart()}
      </Paper>
    </Box>
  );
}
